import type { Campaign, CadPlayoutExtentByEnv } from 'c-sdk';

/**
 * Takes `unknown` because these values come straight off API responses whose shape has changed
 * under us before (the CAD extent went from a string to a per-environment map): anything that
 * isn't a date string gives null rather than crashing the page.
 */
export const parseReportDate = (raw: unknown): Date | null => {
    if (typeof raw !== 'string' || !raw) return null;
    const [year, month, day] = raw.slice(0, 10).split('-').map(Number);
    if (!year || !month || !day) return null;
    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const pickDate = (dates: (Date | null)[], pickEarlier: boolean): Date | null =>
    dates.reduce<Date | null>((acc, d) => {
        if (!d) return acc;
        if (!acc) return d;
        return (pickEarlier ? d < acc : d > acc) ? d : acc;
    }, null);

/**
 * The CAD extent stored for one environment. The legacy scalar only ever held live's, so it
 * answers for live alone. No environment means live, the report's default.
 */
export const getCadPlayoutDate = (
    extent: CadPlayoutExtentByEnv | undefined,
    environment?: string | null,
): string | null => {
    const env = environment || 'live';
    if (extent == null) return null;
    if (typeof extent === 'string') return env === 'live' ? extent : null;
    return extent[env] ?? null;
};

/**
 * Earliest of the campaign start date and the first day of play in the given environment, so the
 * reporting date filter covers plays that happened before the booked start.
 */
export const getCampaignReportStartDate = (
    campaign?: Campaign | null,
    environment?: string | null,
): Date | null =>
    pickDate(
        [
            parseReportDate(campaign?.date_start),
            parseReportDate(
                getCadPlayoutDate(campaign?.min_cad_entry_playout_start_datetime, environment),
            ),
        ],
        true,
    );

/**
 * Latest of the campaign end date and the last day of play in the given environment, so the
 * reporting date filter covers plays that ran on past the booked end.
 */
export const getCampaignReportEndDate = (
    campaign?: Campaign | null,
    environment?: string | null,
): Date | null =>
    pickDate(
        [
            parseReportDate(campaign?.date_end),
            parseReportDate(
                getCadPlayoutDate(campaign?.max_cad_entry_playout_start_datetime, environment),
            ),
        ],
        false,
    );
